/* eslint-disable no-await-in-loop */
// SSE transport for the agent chat. The existing ACH fetchers (src/api/ach/client.ts)
// are JSON-terminal and their 401 path signs the user out + hard-redirects, which
// would kill an in-flight stream and the conversation with it — so this module owns
// its own fetch and turns 401 into a terminal ChatAuthError instead.

import { achBearerToken } from '../../util/achAuth';
import { ChatContext } from './pageContext';

export type ChatEvent =
  | { kind: 'status'; text: string }
  | { kind: 'message'; runId: string; text: string }
  | { kind: 'error'; code: string; message: string }
  | { kind: 'done'; runId: string; truncated: boolean }
  | { kind: 'heartbeat' };

export type ChatVerdict = 'up' | 'down';

export type ChatRequestMessage = { role: string; text: string; runId?: string };

export type ChatRequest = { context: ChatContext; messages: ChatRequestMessage[] };

export class ChatAuthError extends Error {
  constructor(message = 'Session expired — sign in again.') {
    super(message);
    this.name = 'ChatAuthError';
  }
}

const CHAT_PATH = '/api/admin/v2/agent/chat';
const FEEDBACK_PATH = `${CHAT_PATH}/feedback`;

const authHeaders = (): Record<string, string> => {
  const token = achBearerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// The wire is snake_case; the FE types stay camelCase.
const toWire = (req: ChatRequest) => ({
  context: { page_type: req.context.pageType, entities: req.context.entities },
  messages: req.messages.map((m) => ({ role: m.role, text: m.text, ...(m.runId ? { run_id: m.runId } : {}) })),
});

const problemMessage = async (response: Response): Promise<string> => {
  const fallback = `Chat request failed (${response.status})`;
  try {
    const body = await response.json();
    return body?.detail || body?.title || fallback;
  } catch {
    return fallback;
  }
};

const parseFrame = (frame: string): ChatEvent | null => {
  let name = 'message';
  const data: string[] = [];
  frame.split('\n').forEach((line) => {
    if (!line || line.startsWith(':')) return;
    const sep = line.indexOf(':');
    const field = sep === -1 ? line : line.slice(0, sep);
    const value = sep === -1 ? '' : line.slice(sep + 1).replace(/^ /, '');
    if (field === 'event') name = value;
    if (field === 'data') data.push(value);
  });

  let payload: any = {};
  const raw = data.join('\n');
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      payload = {};
    }
  }

  switch (name) {
    case 'status':
      return { kind: 'status', text: String(payload.text ?? '') };
    case 'message':
      return { kind: 'message', runId: String(payload.run_id ?? ''), text: String(payload.text ?? '') };
    case 'error':
      return {
        kind: 'error',
        code: String(payload.code ?? 'error'),
        message: String(payload.message ?? 'The agent hit an error.'),
      };
    case 'done':
      return { kind: 'done', runId: String(payload.run_id ?? ''), truncated: Boolean(payload.truncated) };
    case 'heartbeat':
      return { kind: 'heartbeat' };
    default:
      return null;
  }
};

export async function* streamChat(
  achApiHost: string,
  req: ChatRequest,
  signal: AbortSignal
): AsyncGenerator<ChatEvent> {
  const response = await fetch(`${achApiHost}${CHAT_PATH}`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(toWire(req)),
  });

  if (response.status === 401) throw new ChatAuthError();
  if (!response.ok) throw new Error(await problemMessage(response));
  if (!response.body) throw new Error('Chat response carried no stream.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');
      let end = buffer.indexOf('\n\n');
      while (end !== -1) {
        const event = parseFrame(buffer.slice(0, end));
        buffer = buffer.slice(end + 2);
        if (event) yield event;
        end = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}

// Fire-and-forget: a lost thumbs-up must never surface to the user, and must never
// take the achSendJSON 401 path (signout + redirect) mid-conversation.
export const postFeedback = async (
  achApiHost: string,
  runId: string,
  verdict: ChatVerdict,
  comment?: string
): Promise<void> => {
  try {
    await fetch(`${achApiHost}${FEEDBACK_PATH}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ run_id: runId, verdict, ...(comment ? { comment } : {}) }),
    });
  } catch {
    // Intentionally ignored.
  }
};
